import { encrypt } from 'crypto-js/aes';
import Hex from 'crypto-js/enc-hex';

import {
  IMAGE_CDN_KEY,
  IMAGE_CDN_IV,
  IMAGE_CDN,
  SOURCE_IMAGE_CDN,
  SOURCE_IMAGE_BUCKET,
} from '../../constants/image-cdn';

const key = Hex.parse(IMAGE_CDN_KEY);
const iv = Hex.parse(IMAGE_CDN_IV);

/**
 * @typedef {string} ImageEditionKey - Sharp method name
 * @typedef {*} ImageEditionValue - Sharp method argument
 * @typedef {Object.<ImageEditionKey, ImageEditionValue>} ImageEditions
 */

/**
 * 找出圖片的 S3 key
 * 如果是 [image-processor](https://bitbucket.org/CHOCOLABS/image-compressor) 產生的縮圖則回傳原圖的 key
 *
 * @example
 * getRawImageKey('/path/to/image.png') // => 'path/to/image.png'
 * getRawImageKey('/large/path/to/image.jpg') // => 'raw_images/path/to/image.jpg'
 *
 * @param {string} path
 */
function getRawImageKey(path) {
  return path.replace(/^\/+/, '').replace(/^(?:originals|small|medium|large)(?=\/)/, 'raw_images');
}

/**
 * Encrypt(AES) image CDN payload to cipher text(HEX)
 *
 * @param {object} payload
 * @param {string} payload.bucket - S3 bucket name
 * @param {string} payload.key - S3 object key
 * @param {ImageEditions} payload.edits - image editions
 */
function encryptImageCDNPayload(payload) {
  return encrypt(JSON.stringify(payload), key, { iv }).ciphertext.toString();
}

/**
 * 透過 [image CDN](https://aws.amazon.com/tw/solutions/implementations/serverless-image-handler/) 編輯圖片
 *
 * @example
 * editImage(imageURL, {
 *   resize: { width: 1920 },
 *   toFormat: 'jpeg',
 *   jpeg: { quality: 70, progressive: true },
 *   grayscale: true
 * });
 *
 * @param {string} sourceImageURL
 * @param {ImageEditions} imageEditions - [Sharp Doc](https://sharp.pixelplumbing.com/api-output)
 */
export default function editImage(sourceImageURL, imageEditions) {
  try {
    if (!(imageEditions instanceof Object)) {
      throw new Error('No edition');
    }

    const url = new URL(sourceImageURL, SOURCE_IMAGE_CDN);

    if (url.origin !== SOURCE_IMAGE_CDN) {
      throw new Error('Invalid source image');
    }

    const rawImageKey = getRawImageKey(url.pathname);

    const payload = {
      bucket: SOURCE_IMAGE_BUCKET,
      key: rawImageKey,
      edits: {
        ...imageEditions,
        resize: {
          fit: 'inside',
          withoutEnlargement: true,
          ...imageEditions?.resize,
        },
      },
    };

    return `${IMAGE_CDN}/${encryptImageCDNPayload(payload)}`;
  } catch (e) {
    return sourceImageURL;
  }
}
